import { useEffect, useRef } from 'react';

// #762 / #612 -- one action per Escape press (docs/model/drawing-ui-input-accessibility.md):
// cancel the active gesture first (restoring its start, no Undo entry); otherwise close the
// active menu/dialog; otherwise exit one group level; otherwise clear the selection. Every
// Escape consumer registers here instead of listening for itself, so a single press can no
// longer both cancel a drag and clear the selection.
//
// The arbiter listens in the bubble phase, after element handlers: a field or editor that uses
// Escape itself (the rich-text editor cancels its burst, #609; search clears its suggestions)
// stops or marks the press and the arbiter leaves it alone. From any other text field only an
// overlay may act (Escape still closes the dialog around an input), never the canvas gesture or
// selection.
export const ESCAPE_PRIORITY = { gesture: 0, overlay: 1, group: 2, selection: 3 } as const;
export type EscapePriority = (typeof ESCAPE_PRIORITY)[keyof typeof ESCAPE_PRIORITY];

// Returns true when it acted (the press is then consumed).
export type EscapeHandler = () => boolean;

interface Entry {
  readonly priority: EscapePriority;
  readonly order: number;
  readonly handler: EscapeHandler;
}

const entries = new Set<Entry>();
let nextOrder = 0;
let listening = false;

function isEditable(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
  const fromField = isEditable(event.target);
  // Lower priority first; within a priority the most recently opened (registered) acts first,
  // so the topmost of two open overlays closes.
  const ordered = [...entries]
    .filter((entry) => !fromField || entry.priority === ESCAPE_PRIORITY.overlay)
    .sort((a, b) => a.priority - b.priority || b.order - a.order);
  for (const entry of ordered) {
    if (entry.handler()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }
}

export function registerEscapeHandler(priority: EscapePriority, handler: EscapeHandler): () => void {
  const entry: Entry = { priority, order: nextOrder++, handler };
  entries.add(entry);
  if (!listening && typeof window !== 'undefined') {
    window.addEventListener('keydown', onKeyDown);
    listening = true;
  }
  return () => {
    entries.delete(entry);
    if (entries.size === 0 && listening) {
      window.removeEventListener('keydown', onKeyDown);
      listening = false;
    }
  };
}

// React form: `handler` is read through a ref (always the latest closure); `enabled` scopes the
// registration (e.g. only while an overlay is open, so its order reflects when it opened).
export function useEscapeAction(priority: EscapePriority, handler: EscapeHandler, enabled = true): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    if (!enabled) return;
    return registerEscapeHandler(priority, () => handlerRef.current());
  }, [enabled, priority]);
}

// #762 / #612 -- the platform's command modifier: Cmd on Apple platforms, Ctrl elsewhere
// (accepted: "Use Ctrl on Windows and Cmd on macOS for equivalent commands").
const APPLE_PLATFORM =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

export function isPrimaryModifier(event: { readonly metaKey: boolean; readonly ctrlKey: boolean }): boolean {
  return APPLE_PLATFORM ? event.metaKey : event.ctrlKey;
}

/** #807 -- how a shortcut hint writes that modifier ("⌘" or "Ctrl+"), from the same platform test. */
export function primaryModifierLabel(): string {
  return APPLE_PLATFORM ? '⌘' : 'Ctrl+';
}
